import React from 'react';
import { useNavigate } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { Currency, Header, HeaderMobileComponent, Language, MenuState } from '@agoda/layout';
import { MenuWindowState, setMenuState } from 'store/slice/menu';
import { mapHeaderFeatureConfigToAllCases } from 'component/common/Header/header.selectors';
import { mapDesktopHeaderProps } from 'component/common/Header/DesktopHeader.selectors';
import LazyEarnAcceleratorBanner from 'component/common/EnterpriseWhiteLabel/EarnAcceleratorBanner/LazyEarnAcceleratorBanner';
import { HeaderEventHandlers } from 'component/common/Header/ActivitiesHeader.types';
import { getPathWithCurrency, getPathWithLanguage } from 'core/service/Url/Url';
import { mapMobileHeaderProps } from 'component/common/Header/MobileHeader.selectors';
import { CommonHeaderPropsWrapper } from 'core/type/header.types';
import { findTagByQuery, killEvent, toggleClassNameInTag } from 'core/utils/domUtils';
import { getHeader } from 'core/window/windowService';
import { isCityPage, isDetailPage, isSearchPage } from 'core/constant/analytics/AnalyticsPageType';
import ErrorModal from 'component/common/ErrorModal/ErrorModal';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesOverlayLanguageErrorModalSeenEventActivitiesFieldsV2 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesOverlayLanguageErrorModalSeenEventActivitiesManifestV2 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { DispatchProps, mapDispatchToProps, mapStateToProps, StateProps } from 'component/common/Header/ActivitiesHeader.selectors';
import { Box } from '@drone-js/core';
import { goBack } from 'store/slice/navigation';

import 'component/common/Header/DesktopHeader.scss';
import 'component/common/Header/MobileHeader.scss';
import { WebViewHeader } from 'component/common/Header/WebViewHeader';

const HeaderContainerStyle: React.CSSProperties = { display: 'flex', flex: '1 1 0', flexDirection: 'column' };

type Props = CommonHeaderPropsWrapper & StateProps & DispatchProps;

const ActivitiesHeaderComponent: React.FC<Props> = React.memo(
    ({
        changeCurrency,
        changeLanguage,
        changeLanguageOnDetails,
        header = getHeader(),
        navigationState,
        config,
        menuState,
        screenWidth,
        isAnyOverlayVisible,
        supportedLanguages,
        activityId,
        screenSize,
        isHomePage,
        isAlwaysShowBackButton,
        isRebrandCartToSavedEnabled,
    }) => {
        const headerContainerRef = React.useRef<HTMLElement>(null);

        const dispatch = useDispatch();
        const navigate = useNavigate();
        const [showError, setShowError] = React.useState(false);
        const pageIsDetailPage = isDetailPage(navigationState.pageTypeId);
        const pageIsSearchPage = isSearchPage(navigationState.pageTypeId);
        const pageIsCityPage = isCityPage(navigationState.pageTypeId);
        const showSearchBar = pageIsSearchPage || pageIsDetailPage || pageIsCityPage;

        const closeMobileHeader = (e: Event) => {
            if (menuState === MenuState.Open) {
                killEvent(e);
            }
            dispatch(setMenuState(MenuWindowState.Closed));
        };

        React.useEffect(() => {
            toggleClassNameInTag('body', 'mobile-header-open', menuState === MenuState.Open);

            if (menuState !== MenuState.Open) {
                toggleClassNameInTag('body', 'mobile-header-close', true);

                setTimeout(() => {
                    toggleClassNameInTag('body', 'mobile-header-close', false);
                }, 500);
            }

            const layoutSiteWrapper = findTagByQuery('.Layout__siteWrapper > .PageTransition');
            layoutSiteWrapper && layoutSiteWrapper.addEventListener('click', closeMobileHeader);

            return () => {
                layoutSiteWrapper && layoutSiteWrapper.removeEventListener('click', closeMobileHeader);
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [menuState]);

        React.useEffect(() => {
            closeMobileHeader({} as Event);
            if (headerContainerRef.current) {
                // If we click on any element outside the header, the header will close.
                // We had to use this trick because Desktop header does not expose any listener to detect if user menu drop down is open or close
                headerContainerRef.current.click();
            }

            // we must not use 'closeMobileHeader' as dependency otherwise the mobile header would not open
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [screenWidth]);

        React.useEffect(() => {
            if (pageIsDetailPage && showError) {
                trackElementEvent<ActivitiesOverlayLanguageErrorModalSeenEventActivitiesFieldsV2>(ActivitiesOverlayLanguageErrorModalSeenEventActivitiesManifestV2.ids, {});
            }
        }, [pageIsDetailPage, showError]);

        if (!config.behavior.header.show) {
            return null;
        }

        const isSupportedLanguage = (cultureCode: string) => {
            return supportedLanguages.includes(cultureCode);
        };

        const canChangeLanguage = () => {
            setShowError(pageIsDetailPage);
            return !pageIsDetailPage;
        };

        const onCurrencyChange = (currency: Currency) => {
            const pathWithCurrency = getPathWithCurrency(navigationState, currency, true);
            changeCurrency(activityId, currency, pathWithCurrency);
            return true;
        };

        const onLanguageChange = (language: Language) => {
            const pathWithLanguage = getPathWithLanguage(navigationState, language);
            if (pageIsDetailPage) {
                const isSupportedLang = isSupportedLanguage(language.cultureCode);
                if (isSupportedLang) {
                    changeLanguageOnDetails(activityId, language, pathWithLanguage);
                    return true;
                }
                setShowError(!isSupportedLang);
                return !isSupportedLang;
            }

            if (canChangeLanguage()) {
                changeLanguage(pathWithLanguage);
                return true;
            }
            return false;
        };

        mapHeaderFeatureConfigToAllCases(header, config.behavior.header);
        const handlers: HeaderEventHandlers = { onCurrencyChange, onLanguageChange, dispatch, navigate };
        const desktopHeaderProps = mapDesktopHeaderProps(navigationState, handlers, header, config);
        const mobileHeaderProps = mapMobileHeaderProps(navigationState, handlers, header, config, menuState, isAlwaysShowBackButton);

        if (config.isWebView && config.behavior.header.show) {
            const backButtonOnClick = () => handlers.dispatch(goBack({ config, navigate: handlers.navigate }));

            return (
                <WebViewHeader
                    headerContainerRef={headerContainerRef}
                    backButtonOnClick={backButtonOnClick}
                    showSearchBar={showSearchBar}
                    showCartIcon={mobileHeaderProps.showCartIcon || false}
                    isRebrandCartToSavedEnabled={isRebrandCartToSavedEnabled || false}
                />
            );
        }

        return (
            <>
                <LazyEarnAcceleratorBanner />
                <Box
                    backgroundColor={config.behavior.header.isBlackHeaderBackground ? 'product.primary' : 'base.surface'}
                    data-testid="header-container"
                    display="flex"
                    position={showSearchBar ? 'relative' : 'sticky'}
                    top={showSearchBar ? undefined : '0'}
                    zIndex={4}
                    ref={headerContainerRef}
                >
                    {!screenSize.desktop ? (
                        <div style={HeaderContainerStyle} aria-hidden={isAnyOverlayVisible}>
                            <HeaderMobileComponent {...mobileHeaderProps} menuState={menuState} />
                        </div>
                    ) : (
                        <div style={HeaderContainerStyle} aria-hidden={isAnyOverlayVisible}>
                            <Header {...desktopHeaderProps} />
                        </div>
                    )}
                    <ErrorModal
                        key="can-not-change-language-or-currency"
                        elementName="activities-language-or-currency-error-modal"
                        showModal={showError}
                        message={pageIsDetailPage ? config.cms[292000] : config.cms[142725]}
                        buttonText={config.cms[126815]}
                        onButtonClick={() => setShowError(false)}
                    />
                </Box>
            </>
        );
    },
);
ActivitiesHeaderComponent.displayName = 'ActivitiesHeaderComponent';

const ActivitiesHeader = connect(mapStateToProps, mapDispatchToProps)(ActivitiesHeaderComponent);
ActivitiesHeader.displayName = 'ActivitiesHeader';

export { ActivitiesHeader, ActivitiesHeaderComponent };
